/**
 * Hook for fetching trade history (fills) from backend
 */

import { useQuery } from "@tanstack/react-query";
import { FillsApi } from "../api-client";

export interface Fill {
  id: string;
  user_id: string;
  market: string;
  side: "buy" | "sell";
  size: string | number;
  price: string | number;
  fee: string | number;
  realized_pnl: string | number;
  fill_type: string;
  created_at: string;
}

/**
 * Fetch fills (trade history) for authenticated user
 * Uses listAllFills to get complete history for analytics
 */
const fetchFills = async (): Promise<Fill[]> => {
  try {
    const fills = await FillsApi.listAllFills();
    return fills as unknown as Fill[];
  } catch (error) {
    console.error("[useTradeHistory] Error fetching fills:", error);
    throw new Error("Failed to fetch trade history");
  }
};

export const useTradeHistory = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["trade-history", userId],
    queryFn: () => (userId ? fetchFills() : Promise.resolve([])),
    enabled: !!userId,
    // Industry standard for trading platforms: always fetch fresh data
    staleTime: 0, // Always consider data stale (refetch on mount)
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache longer for better UX
    refetchOnMount: true, // Explicit refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus (too aggressive)
    refetchOnReconnect: true, // Refetch when network reconnects
  });
};

